import { useState } from 'react';
import classnames from 'classnames';
import { Blueprint } from '@leverege/molten/imagine';
import { Toast } from '@leverege/ui-elements';
import ConfirmRecovrSale from '../screens/ConfirmRecovrSale';
import CustomerAccount from '../screens/CustomerAccount';
import SaleError from '../screens/SaleError';
import SaleInformation from '../screens/SaleInformation';
import { getContractPDF, getSale } from '../../../util/Interface';
import RecovrSales from '../../../util/RecovrSales';
import { UNPAIRED } from '../../../constants';
import C from '../Constants';
import S from './RecovrAndVehicle.css';
import { useUserFormErrors } from '../components/UserForm';
import RecovrInformation from '../components/RecovrInformation';
import RecovrTagsInformation from '../components/RecovrTagsInformation';
import RecovrKeysInformation from '../components/RecovrKeysInformation';
import DownloadPDF from '../components/DownloadPDF';
import Util from '../Util';
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
export default function RecovrAndVehicle(props) {
  const {
    onClose,
    onChange,
    actions,
    vehicle,
    saleData,
    location,
    setLoadingMessage
  } = props;
  const [newSaleId, setNewSaleId] = useState();
  const [currentStepId, setCurrentStepId] = useState('sale-information');
  const [error, setError] = useState(false);
  const {
    errors,
    setValidateOnDataChange,
    getValidationErrors
  } = useUserFormErrors({
    data: saleData?.newAcct,
    isExAcct: false
  });
  const {
    errors: exAcctErrors,
    setValidateOnDataChange: setExAcctValidateOnDataChange,
    getValidationErrors: getExAcctValidationErrors
  } = useUserFormErrors({
    data: saleData?.exAcct,
    isExAcct: true
  });
  const locator = saleData?.newLocator || vehicle?.data?.data?.locator;
  const locatorBlueprint = Blueprint.getByType('locator');
  const tagBlueprint = Blueprint.getByType('tag');
  const productTypes = saleData?.productTypes;
  const goToStep = stepId => () => {
    setCurrentStepId(stepId);
  };
  const recovrInformationCheckDisabled = locator => {
    if (locator) {
      if (locator.id === UNPAIRED) {
        return false;
      }
    }
    return !locator || !Util.isLocatorActivated(locator) || Util.isLocatorLowBattery(locator) || Util.isLocatorOffline(locator);
  };
  const recovrTagsInformationCheckDisabled = saleData => {
    if (saleData?.keys) {
      return !Object.values(saleData?.keys).some(keyData => keyData.sell && keyData.tag?.id);
    }
  };
  const submitSale = async () => {
    setLoadingMessage('Completing Sale');
    try {
      const {
        saleId
      } = await RecovrSales.sell({
        ...saleData,
        actions,
        vehicle,
        hasCustomer: true,
        vehicleLeftLot: false,
        toast: false,
        saleType: C.SALE_TYPES.VEHICLE_WITH_LOCATOR,
        goToSales: false,
        throwErr: true,
        productTypes,
        keys: saleData.keys ? Object.values(saleData.keys).reduce((prev, keyData) => {
          if (keyData.sell && keyData.tag?.id) {
            prev[keyData.key.id] = {
              keyId: keyData.key.id,
              tagId: keyData.tag.id,
              tagSaleStatus: keyData.tagSaleStatus
            };
          }
          return prev;
        }, {}) : null
      });
      setNewSaleId(saleId);
      goToStep('download-pdf')();
    } catch (err) {
      goToStep('retry-sale')();
    }
    setLoadingMessage(null);
  };
  const downloadContractPDF = saleId => async ({
    productType
  }) => {
    const {
      data: {
        systemId,
        data: {
          company,
          location
        } = {}
      } = {}
    } = vehicle;
    const sale = await getSale(systemId, company.id, location.id, saleId);
    const keysCount = sale?.data?.keysCount || 1;
    // eslint-disable-next-line max-len
    const filename = `RecovR ${productType === 'recovrKeys' ? `${keysCount > 1 ? 'Tags' : 'Tag'}` : 'Locator'} Agreement - ${sale?.data?.name}.pdf`;
    setLoadingMessage('Generating Contract');
    try {
      await getContractPDF({
        systemId,
        companyId: company.id,
        locationId: location.id,
        saleId,
        filename,
        productType
      });
      onClose();
    } catch (err) {
      goToStep('pdf-download-failed')();
      Toast.error('Customer Contract Error');
    }
    setLoadingMessage(null);
  };
  const steps = {
    'sale-information': {
      submitText: 'Next',
      submitDisabled: !(productTypes.recovr || productTypes.recovrKeys),
      onSubmit: () => {
        if (productTypes.recovr) {
          goToStep('recovr-information')();
        } else if (productTypes.recovrKeys) {
          goToStep('recovr-keys-information')();
        } else {
          Toast.error(['No product type selected', 'Select a product type to continue']);
        }
      },
      contentClass: classnames(S.content, S.saleInformationContent),
      content: () => /*#__PURE__*/_jsx(SaleInformation, {
        ...saleData,
        vehicle: vehicle,
        location: location,
        actions: actions,
        locatorBlueprint: locatorBlueprint,
        onChange: onChange
      })
    },
    'recovr-information': {
      submitText: 'Next',
      submitDisabled: recovrInformationCheckDisabled(locator),
      onSubmit: () => {
        if (productTypes.recovrKeys) {
          goToStep('recovr-keys-information')();
        } else {
          goToStep('customer-information')();
        }
      },
      content: () => /*#__PURE__*/_jsx(RecovrInformation, {
        ...saleData,
        location: location,
        actions: actions,
        locatorBlueprint: locatorBlueprint,
        vehicle: vehicle,
        locator: locator,
        onChange: onChange
      })
    },
    'recovr-keys-information': {
      submitText: 'Next',
      submitDisabled: !(saleData?.knowTags !== null && saleData?.knowTags !== undefined && saleData?.qtyOfTags),
      onSubmit: () => {
        if (saleData?.knowTags) {
          goToStep('recovr-tags-information')();
        } else {
          goToStep('customer-information')();
        }
      },
      content: () => /*#__PURE__*/_jsx(RecovrKeysInformation, {
        ...saleData,
        actions: actions,
        location: location,
        vehicle: vehicle,
        onChange: onChange,
        tagBlueprint: tagBlueprint
      })
    },
    'recovr-tags-information': {
      submitText: 'Next',
      submitDisabled: recovrTagsInformationCheckDisabled(saleData),
      onSubmit: () => {
        let numberOfTags = 0;
        Object.values(saleData?.keys).forEach(keyData => {
          if (keyData.sell && keyData.tag?.id) {
            numberOfTags++;
          }
        });
        if (numberOfTags === saleData?.qtyOfTags) {
          goToStep('customer-information')();
        } else {
          setError(true);
        }
      },
      content: () => /*#__PURE__*/_jsx(RecovrTagsInformation, {
        ...saleData,
        actions: actions,
        location: location,
        vehicle: vehicle,
        onChange: onChange,
        tagBlueprint: tagBlueprint,
        error: error
      })
    },
    'customer-information': {
      submitText: 'Next',
      submitDisabled: saleData?.isNewCustomer ? !saleData?.newAcct : !saleData?.exAcct?.email,
      onSubmit: () => {
        if (saleData?.isNewCustomer) {
          const validationErrors = getValidationErrors();
          if (Object.keys(validationErrors).length > 0) {
            setValidateOnDataChange(true);
            return;
          }
        } else {
          const validationErrors = getExAcctValidationErrors();
          if (Object.keys(validationErrors).length > 0) {
            setExAcctValidateOnDataChange(true);
            return;
          }
        }
        goToStep('review-sale-information')();
      },
      contentClass: classnames(S.content, S.customerAccountContent),
      content: () => /*#__PURE__*/_jsx(CustomerAccount, {
        ...saleData,
        newAcctErrors: errors,
        location: location,
        onChange: onChange,
        exAcctErrors: exAcctErrors
      })
    },
    'review-sale-information': {
      submitText: 'Complete Sale',
      submitDisabled: false,
      onSubmit: submitSale,
      contentClass: classnames(S.content, S.confirmScreenContent),
      content: () => /*#__PURE__*/_jsx(ConfirmRecovrSale, {
        ...saleData,
        vehicle: vehicle,
        locatorBlueprint: locatorBlueprint
      })
    },
    'retry-sale': {
      submitText: 'Try Again',
      submitDisabled: false,
      onSubmit: submitSale,
      contentClass: classnames(S.content, S.saleErrorContent),
      content: () => /*#__PURE__*/_jsx(SaleError, {})
    },
    'download-pdf': {
      submit: /*#__PURE__*/_jsx(DownloadPDF, {
        downloadContractPDF: downloadContractPDF(newSaleId),
        productTypes: productTypes,
        submitText: "Download PDF",
        submitIcon: "https://recovr-ui-assets.storage.googleapis.com/download-icon.png"
      }),
      cancelText: 'Close',
      onSubmit: downloadContractPDF(newSaleId),
      contentClass: classnames(S.content, S.downloadPDFContent),
      content: () => /*#__PURE__*/_jsx(_Fragment, {
        children: "Congrats on the RecovR sale! You can download the RecovR Customer Contract now or at a later time."
      })
    },
    'pdf-download-failed': {
      submitText: 'Close',
      submitDisabled: false,
      onSubmit: onClose,
      cancelHidden: true,
      contentClass: classnames(S.content, S.downloadPDFContent),
      content: () => /*#__PURE__*/_jsx(_Fragment, {
        children: "Uh-oh, looks like something went wrong, please locate the vehicle in the Sales page to download the customer contract."
      })
    }
  };
  return {
    title: 'RecovR & Vehicle Sale',
    currentStep: steps[currentStepId] || {}
  };
}